import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useBranding } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Lock, Mail, User, Eye, EyeOff, AlertCircle, CheckCircle, UserPlus, Upload, FileText, PenTool, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { DocumentSignerDialog } from '../components/SignaturePad';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const inviteCodeFromUrl = searchParams.get('invite');
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(inviteCodeFromUrl || '');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingInvite, setCheckingInvite] = useState(false);
  const [error, setError] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [registrationMode, setRegistrationMode] = useState('invite'); // 'invite' or 'public'
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(false);
  
  // Registration documents state
  const [regDocs, setRegDocs] = useState([]);
  const [regDocContents, setRegDocContents] = useState({}); // {reg_doc_id: {content, file_type}}
  const [regDocSignatures, setRegDocSignatures] = useState({}); // { reg_doc_id: { signature_data, position } }
  const [signerDialogDoc, setSignerDialogDoc] = useState(null); // reg doc currently being signed
  const [regDocFields, setRegDocFields] = useState({}); // {reg_doc_id: [fields]}
  
  const { register, loginWithGoogle } = useAuth();
  const { appName, logoUrl, customText, headingFont } = useBranding();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  // Check if this is the first user (no invite needed)
  useEffect(() => {
    const checkFirstUser = async () => {
      try {
        // Try to register without invite - if first user, it will work
        // This is a simple check by calling a health-like endpoint
        const response = await fetch(`${API_URL}/api/users`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        // If we get 401, it means there are users but we're not authenticated
        // If we get 403, same thing
        // If we get users, there are existing users
        if (response.status === 401 || response.status === 403) {
          // There are users, need invite
          setIsFirstUser(false);
        }
      } catch (error) {
        // Assume first user if we can't check
        console.log('Could not check user status');
      }
    };
    
    // Don't automatically assume first user - let the mode toggle show
    if (inviteCodeFromUrl) {
      setRegistrationMode('invite');
    }
    
    // Fetch registration documents
    const fetchRegDocs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/registration-documents`);
        if (response.ok) {
          const docs = await response.json();
          setRegDocs(docs);
          // Fetch content and signature fields for each document in parallel
          await Promise.all(docs.map(async (doc) => {
            try {
              const contentResp = await fetch(`${API_URL}/api/registration-documents/${doc.reg_doc_id}/content`);
              if (contentResp.ok) {
                const contentData = await contentResp.json();
                setRegDocContents(prev => ({ ...prev, [doc.reg_doc_id]: contentData }));
              }
            } catch { /* ignore */ }
            try {
              const fieldsResp = await fetch(`${API_URL}/api/registration-documents/${doc.reg_doc_id}/signature-fields`);
              if (fieldsResp.ok) {
                const fieldsData = await fieldsResp.json();
                if (fieldsData.fields?.length > 0) {
                  setRegDocFields(prev => ({ ...prev, [doc.reg_doc_id]: fieldsData.fields }));
                }
              }
            } catch { /* ignore */ }
          }));
        }
      } catch (err) { console.error('Failed to fetch registration docs:', err); }
    };
    fetchRegDocs();
  }, [inviteCodeFromUrl]);

  // Validate invite code when provided
  useEffect(() => {
    const validateInvite = async () => {
      if (!inviteCode || inviteCode.length < 10) {
        setInviteInfo(null);
        return;
      }
      
      setCheckingInvite(true);
      try {
        const response = await fetch(`${API_URL}/api/auth/check-invite/${inviteCode}`);
        if (response.ok) {
          const data = await response.json();
          setInviteInfo(data);
          setEmail(data.email);
          setError('');
        } else {
          const errorData = await response.json();
          setInviteInfo(null);
          setError(errorData.detail || 'Invalid invite code');
        }
      } catch (err) {
        setInviteInfo(null);
      } finally {
        setCheckingInvite(false);
      }
    };
    
    validateInvite();
  }, [inviteCode]);

  const passwordRequirements = [
    { label: t('auth.passwordRequirements.length'), met: password.length >= 8 },
    { label: t('auth.passwordRequirements.uppercase'), met: /[A-Z]/.test(password) },
    { label: t('auth.passwordRequirements.number'), met: /\d/.test(password) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('errors.passwordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('errors.weakPassword'));
      return;
    }

    // Check all registration documents are signed
    const requiredSigDocs = regDocs.filter(d => d.requires_signature);
    const unsignedDocs = requiredSigDocs.filter(d => !regDocSignatures[d.reg_doc_id]);
    if (unsignedDocs.length > 0) {
      setError(`Please sign all required documents: ${unsignedDocs.map(d => d.title).join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      let response;
      let data;

      if (registrationMode === 'public') {
        // Public registration
        const formData = new FormData();
        formData.append('email', email);
        formData.append('password', password);
        formData.append('name', name);

        response = await fetch(`${API_URL}/api/auth/register-public`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Registration failed');
        }

        // Submit signatures BEFORE checking pending status
        if (Object.keys(regDocSignatures).length > 0) {
          try {
            await fetch(`${API_URL}/api/registration-documents/sign`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                signer_name: name,
                signer_email: email,
                signatures: Object.entries(regDocSignatures).map(([reg_doc_id, sigInfo]) => ({
                  reg_doc_id,
                  signature_data: sigInfo.signature_data,
                  position: sigInfo.position || null,
                  signer_typed_name: sigInfo.signer_typed_name || null,
                  signed_at_display: sigInfo.signed_at_display || null,
                  name_position: sigInfo.name_position || null,
                  field_values: sigInfo.field_values || null,
                  sig_scale: sigInfo.sig_scale || 1,
                  name_scale: sigInfo.name_scale || 1
                }))
              })
            });
          } catch (err) { console.error('Failed to submit signatures:', err); }
        }

        if (data.status === 'pending') {
          setPendingStatus(true);
          toast.success('Registration submitted! Please wait for admin approval.');
          return;
        }
      } else {
        // Invite-based registration
        response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email,
            password,
            name,
            invite_code: inviteCode || null
          })
        });

        data = await response.json();

        if (!response.ok) {
          let errorMessage = data.detail || 'Registration failed';
          if (response.status === 403) {
            errorMessage = 'An invitation code is required to register. Please contact an administrator or use public registration.';
          } else if (response.status === 400) {
            errorMessage = data.detail || 'Invalid registration data. Please check your inputs.';
          } else if (response.status === 409) {
            errorMessage = 'This email is already registered. Please try logging in instead.';
          }
          throw new Error(errorMessage);
        }
      }

      toast.success(t('success.accountCreated') || 'Account created successfully!');
      
      // Submit registration document signatures (for invite-based registration)
      if (registrationMode !== 'public' && Object.keys(regDocSignatures).length > 0) {
        try {
          await fetch(`${API_URL}/api/registration-documents/sign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              signer_name: name,
              signer_email: email,
              signatures: Object.entries(regDocSignatures).map(([reg_doc_id, sigInfo]) => ({
                reg_doc_id,
                signature_data: sigInfo.signature_data,
                position: sigInfo.position || null,
                signer_typed_name: sigInfo.signer_typed_name || null,
                signed_at_display: sigInfo.signed_at_display || null,
                name_position: sigInfo.name_position || null,
                field_values: sigInfo.field_values || null,
                sig_scale: sigInfo.sig_scale || 1,
                name_scale: sigInfo.name_scale || 1
              }))
            })
          });
        } catch (err) { console.error('Failed to submit signatures:', err); }
      }
      
      // Store token
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
      
      navigate('/dashboard');
    } catch (err) {
      const errorMsg = err.message || 'Registration failed. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachmentFile(file);
    }
  };

  // Show pending status page
  if (pendingStatus) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-yellow-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Registration Submitted!
          </h1>
          <p className="text-white/60 mb-6">
            Your registration is pending approval by an administrator. 
            You will be notified once your account has been reviewed.
          </p>
          <Link to="/login">
            <Button variant="outline" className="border-white/10 text-white hover:bg-white/5">
              Return to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 flex items-center gap-1 bg-white/5 border border-white/10 rounded-full px-1 py-0.5" data-testid="language-switcher">
          <Globe className="w-3.5 h-3.5 text-white/40 ml-1.5" />
          {[{ code: 'en', label: 'EN' }, { code: 'fr', label: 'FR' }, { code: 'ht', label: 'HT' }].map(lang => (
            <button key={lang.code} onClick={() => setLanguage(lang.code)}
              className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${language === lang.code ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
              data-testid={`lang-${lang.code}`}>{lang.label}</button>
          ))}
        </div>
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-8 w-auto" />
            ) : (
              <Shield className="w-8 h-8 text-primary" />
            )}
            <span className="font-bold text-xl text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {appName}
            </span>
          </Link>

          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: headingFont ? `'${headingFont}', sans-serif` : 'Space Grotesk, sans-serif' }}>
            {customText?.register_title || t('auth.signUp')}
          </h1>
          <p className="text-white/60 mb-6">
            {customText?.register_subtitle || t('auth.signUpDescription')}
          </p>

          {/* Invite Info Banner */}
          {inviteInfo && (
            <div className="flex items-start gap-3 p-4 rounded-sm bg-primary/10 border border-primary/30 mb-6">
              <UserPlus className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">You've been invited!</p>
                <p className="text-white/60 text-sm">
                  Invited by {inviteInfo.invited_by} as <span className="text-primary capitalize">{inviteInfo.role.replace('_', ' ')}</span>
                </p>
              </div>
            </div>
          )}

          {/* No Invite Warning */}
          {!inviteCode && !isFirstUser && (
            <div className="flex items-start gap-3 p-4 rounded-sm bg-yellow-500/10 border border-yellow-500/30 mb-6">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Registration Options</p>
                <p className="text-white/60 text-sm">
                  Have an invite code? Use it below. Otherwise, you can request access via public registration.
                </p>
              </div>
            </div>
          )}

          {/* Registration Mode Toggle */}
          {!inviteCode && !isFirstUser && (
            <div className="flex gap-2 mb-6">
              <Button
                type="button"
                variant={registrationMode === 'invite' ? 'default' : 'outline'}
                onClick={() => setRegistrationMode('invite')}
                className={registrationMode === 'invite' 
                  ? 'flex-1 bg-primary text-black' 
                  : 'flex-1 border-white/10 text-white hover:bg-white/5'}
              >
                With Invite Code
              </Button>
              <Button
                type="button"
                variant={registrationMode === 'public' ? 'default' : 'outline'}
                onClick={() => setRegistrationMode('public')}
                className={registrationMode === 'public' 
                  ? 'flex-1 bg-primary text-black' 
                  : 'flex-1 border-white/10 text-white hover:bg-white/5'}
              >
                Request Access
              </Button>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-red-400 mb-6">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Invite Code Field (for invite mode) */}
            {!inviteCodeFromUrl && registrationMode === 'invite' && (
              <div className="space-y-2">
                <Label htmlFor="inviteCode" className="text-white/80">Invite Code</Label>
                <div className="relative">
                  <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="inviteCode"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="Enter invite code (or leave empty for first user)"
                    className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-primary h-11"
                    data-testid="register-invite-code-input"
                  />
                  {checkingInvite && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {inviteInfo && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/80">{t('auth.fullName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-primary h-11"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-primary h-11"
                  required
                  disabled={!!inviteInfo} // Disable if email comes from invite
                  data-testid="register-email-input"
                />
              </div>
              {inviteInfo && (
                <p className="text-xs text-white/40">Email is set by your invitation</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-primary h-11"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/50"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="space-y-1 pt-2">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    {req.met ? (
                      <CheckCircle className="w-3 h-3 text-primary" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-white/20" />
                    )}
                    <span className={req.met ? 'text-primary' : 'text-white/40'}>{req.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80">{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-primary h-11"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            {/* Registration Documents - Preview & Signature Section */}
            {regDocs.length > 0 && (
              <div className="space-y-3 pt-2" data-testid="registration-docs-section">
                <div className="flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-primary" />
                  <Label className="text-white/80">Required Documents</Label>
                </div>
                <p className="text-white/40 text-xs">Review and sign each document by clicking the Sign button</p>
                {regDocs.map(doc => {
                  const sigInfo = regDocSignatures[doc.reg_doc_id];
                  return (
                    <div key={doc.reg_doc_id} className="border border-white/10 rounded-sm overflow-hidden" data-testid={`reg-doc-sign-${doc.reg_doc_id}`}>
                      <div className="flex items-center justify-between p-3 bg-black/20">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-white text-sm font-medium truncate">{doc.title}</p>
                            {doc.description && <p className="text-white/40 text-xs truncate">{doc.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {sigInfo ? (
                            <div className="flex items-center gap-2">
                              <img src={sigInfo.signature_data} alt="Signed" className="h-5 object-contain bg-white rounded-sm px-1" />
                              <CheckCircle className="w-4 h-4 text-primary" />
                              <button
                                type="button"
                                onClick={() => setSignerDialogDoc(doc)}
                                className="text-xs text-white/40 hover:text-white underline"
                              >
                                Re-sign
                              </button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => setSignerDialogDoc(doc)}
                              disabled={!regDocContents[doc.reg_doc_id]?.content}
                              className="bg-primary text-black hover:bg-primary/90 h-8 text-xs disabled:opacity-40"
                              data-testid={`sign-btn-${doc.reg_doc_id}`}
                            >
                              <PenTool className="w-3 h-3 mr-1" /> {regDocContents[doc.reg_doc_id]?.content ? 'Sign Document' : 'Loading...'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* DocuSign-like Signer Dialog for Registration Documents */}
            {signerDialogDoc && (
              <DocumentSignerDialog
                open={!!signerDialogDoc}
                onOpenChange={(v) => { if (!v) setSignerDialogDoc(null); }}
                documentContent={regDocContents[signerDialogDoc.reg_doc_id]?.content || null}
                documentType={regDocContents[signerDialogDoc.reg_doc_id]?.file_type || ''}
                documentName={signerDialogDoc.title}
                existingSignatures={[]}
                signatureFields={regDocFields[signerDialogDoc.reg_doc_id] || []}
                onSign={(sigData, position, metadata) => {
                  setRegDocSignatures(prev => ({
                    ...prev,
                    [signerDialogDoc.reg_doc_id]: {
                      signature_data: sigData,
                      position: position || null,
                      signer_typed_name: metadata?.signer_typed_name || null,
                      signed_at_display: metadata?.signed_at_display || null,
                      name_position: metadata?.name_position || null,
                      field_values: metadata?.field_values || null,
                      sig_scale: metadata?.sig_scale || 1,
                      name_scale: metadata?.name_scale || 1
                    }
                  }));
                  setSignerDialogDoc(null);
                }}
              />
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider btn-glow h-11"
              data-testid="register-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.signUp')}
            </Button>
          </form>

          <p className="text-center text-white/40 mt-8">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZXJ2ZXIlMjByb29tJTIwZGFyayUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzM1MTgyOTF8MA&ixlib=rb-4.1.0&q=85"
          alt="Server Room"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#050505]/80" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <Lock className="w-20 h-20 text-primary mx-auto mb-6 animate-glow-pulse" />
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Private & Secure
            </h2>
            <p className="text-white/60 max-w-sm">
              {inviteInfo 
                ? `Welcome! You've been invited to join ${appName}.`
                : `${appName} is a private document vault. Registration is by invitation only.`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
